/**
 * The post's text, as LinkedIn will receive it.
 *
 * WHY THIS EXISTS
 * ---------------
 * The confirmation step promises the member that what they are looking at is what will be posted.
 * That promise was false: the dialog showed the raw contents of the text box, while the server
 * normalised the text and appended the record's verification address when the member had not
 * written it themselves. So the member confirmed one thing and LinkedIn received another — a longer
 * post, with a line they never saw. A confirmation screen that disagrees with the request is worse
 * than no confirmation screen, because it is believed.
 *
 * The rule is the backend's (`backend/src/domain/linkedin/member-post.ts`), and it stays the
 * backend's: this module does not decide anything, it reproduces. The two are held together by
 * `tests/fixtures/linkedin-commentary-vectors.json`, which both packages run against field by
 * field — the same arrangement PO 7.9 uses for the Add-to-Profile link. If the server's rule ever
 * changes, the vectors change with it and this module fails until it agrees again.
 *
 * The appended address is NOT a decoration to be dropped for the preview's convenience: a
 * certificate post without its verification link is the one thing a reader actually needs, and the
 * brief keeps that rule. What changes here is only that the member sees it before they agree to it.
 */

/** LinkedIn's own limit for a post's commentary, mirrored from the backend's domain constant. */
export const COMMENTARY_MAX_LENGTH = 3000;

export interface CommentaryInput {
  /** The text the member edited in the dialog, and nothing generated after they last saw it. */
  text: string;
  /** The record's canonical public address, as the SERVER gave it for this draft. */
  canonicalUrl: string;
}

export type CommentaryResult =
  | { ok: true; commentary: string }
  | { ok: false; reason: 'empty' | 'too_long' | 'control_characters' };

/**
 * The backend's character class, expressed as a scan rather than a pattern.
 *
 * The rule is the server's: the C0 controls are refused EXCEPT tab, line feed and carriage return,
 * which a post legitimately contains. Written as a loop because a regular expression carrying those
 * code points is what this package's linter exists to catch — and because the set is easier to read
 * as the four ranges it actually is. The shared vectors are what hold this to the server's rule.
 */
function hasControlCharacter(text: string): boolean {
  for (const character of text) {
    const code = character.codePointAt(0) as number;
    if (code <= 0x08 || code === 0x0b || code === 0x0c || (code >= 0x0e && code <= 0x1f)) return true;
  }
  return false;
}

/**
 * The member's own words, with the record's verification address appended when they have not
 * already included it. Nothing else is added: no hashtag, no product line, no tracking parameter.
 *
 * The length is counted in characters — `[...s].length`, not `s.length` — so an emoji costs one,
 * the way it does on the server.
 */
export function buildCommentary(input: CommentaryInput): CommentaryResult {
  const text = input.text.replace(/\r\n/g, '\n').trim();
  if (!text) return { ok: false, reason: 'empty' };
  if (hasControlCharacter(text)) return { ok: false, reason: 'control_characters' };
  const commentary = text.includes(input.canonicalUrl) ? text : `${text}\n${input.canonicalUrl}`;
  if ([...commentary].length > COMMENTARY_MAX_LENGTH) return { ok: false, reason: 'too_long' };
  return { ok: true, commentary };
}
